import { useMemo, useState, useEffect } from 'react';
import { distributeItemsToColumns, getColumnCount, getImageHeight } from './utils';

/**
 * React hook that distributes items into masonry columns.
 * Only recalculates when items or column count changes.
 * 
 * @param {Object} options
 * @param {Array} options.items - Items to distribute
 * @param {number} [options.columnCount] - Fixed column count (overrides responsive)
 * @param {number} [options.baseColumns] - Base column count from database (used as max, scales down responsively)
 * @param {Object} [options.breakpoints] - Custom breakpoints from database (optional)
 * @param {Function} [options.getItemHeight] - Function to estimate item height
 * @param {React.RefObject} [options.containerRef] - Container ref for responsive width
 * @returns {{ columns: Array<Array>, columnCount: number }}
 */
export function useMasonryColumns({
    items,
    columnCount: fixedColumnCount,
    baseColumns,
    breakpoints: customBreakpoints,
    getItemHeight = getImageHeight,
    containerRef,
}) {
    // Initialize with SSR-safe default (baseColumns or 5)
    // IMPORTANT: Must match server render to avoid hydration mismatch!
    // We must use the same value on server and client initial render,
    // then update to the responsive value in useEffect
    const ssrColumnCount = fixedColumnCount || baseColumns || 5;
    
    // Initialize with SSR-safe value to prevent hydration mismatch
    const [dynamicColumnCount, setDynamicColumnCount] = useState(ssrColumnCount);

    // Watch container width for responsive column count
    useEffect(() => {
        // If fixed column count is provided, don't watch for resize
        if (fixedColumnCount) {
            setDynamicColumnCount(fixedColumnCount);
            return;
        }

        // If no container ref, use window width
        const getWidth = () => {
            if (containerRef?.current) {
                return containerRef.current.offsetWidth;
            }
            if (typeof window !== 'undefined') {
                return window.innerWidth;
            }
            return 1400; // Default for SSR
        };

        const updateColumnCount = () => {
            const width = getWidth();
            const newCount = getColumnCount(width, customBreakpoints, baseColumns);
            setDynamicColumnCount(newCount);
        };

        // Initial calculation
        updateColumnCount();

        // Watch for resize
        if (containerRef?.current) {
            const resizeObserver = new ResizeObserver(updateColumnCount);
            resizeObserver.observe(containerRef.current);
            return () => resizeObserver.disconnect();
        } else if (typeof window !== 'undefined') {
            window.addEventListener('resize', updateColumnCount);
            return () => window.removeEventListener('resize', updateColumnCount);
        }
    }, [fixedColumnCount, baseColumns, customBreakpoints, containerRef]);

    const columnCount = fixedColumnCount || dynamicColumnCount;

    // Distribute items into columns - only recalculates when items or columnCount changes
    const columns = useMemo(() => {
        if (!items || items.length === 0) {
            return Array.from({ length: columnCount }, () => []);
        }
        return distributeItemsToColumns(items, columnCount, getItemHeight);
    }, [items, columnCount, getItemHeight]);

    return {
        columns,
        columnCount,
    };
}

export default useMasonryColumns;
